import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, tap } from 'rxjs';
import { environment } from '../../environments/environment';

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string | null;
  lastName: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

/** A finished login carries `token`; while the second factor is still open there is an `mfaToken` instead
 * (for the /auth/mfa/** calls) plus what is left to do. */
export interface AuthResponse {
  token: string | null;
  mfaRequired: boolean;
  mfaSetupRequired: boolean;
  mfaToken: string | null;
}

export interface MfaSetupData {
  /** Base32 shared key, for typing it into the authenticator app by hand. */
  secret: string;
  otpauthUri: string;
  /** PNG data URL of the QR code for otpauthUri. */
  qrDataUrl: string;
}

export interface MfaConfirmResponse {
  /** Only set when the setup happened during login (no session yet). */
  token: string | null;
  recoveryCodes: string[];
}

export interface MfaStatus {
  enabled: boolean;
  requiredByRole: boolean;
  enforced: boolean;
  /** First day the second factor is mandatory (yyyy-MM-dd); null = no deadline. */
  enforcedFrom: string | null;
  recoveryCodesLeft: number;
}

interface JwtPayload {
  sub?: string;
  roles?: string[];
  firstName?: string;
  lastName?: string;
  exp?: number;
}

export interface CurrentUser {
  email: string;
  firstName: string | null;
  lastName: string | null;
  roles: string[];
}

const TOKEN_STORAGE_KEY = 'auth_token';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly apiUrl = `${environment.apiUrl}/auth`;

  private readonly sessionExpiredSource = new Subject<void>();
  readonly sessionExpired$ = this.sessionExpiredSource.asObservable();
  private readonly loggedInSource = new Subject<void>();
  /** Fires whenever a token is stored (login, register, token renewal) - components already mounted at
   * login time (e.g. the landing page) can't rely on ngOnInit re-running, since no navigation happens when
   * the login dialog closes, so they need this to react to the auth state change. */
  readonly loggedIn$ = this.loggedInSource.asObservable();
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly http: HttpClient) {
    this.scheduleExpiryCheck();
    window.addEventListener('storage', this.onStorageChange);
  }

  /**
   * Keeps tabs in sync: another tab logging in/out or renewing its token rewrites the same localStorage
   * key, so this recomputes the expiry timer for whatever token is now current. UI reads (isLoggedIn,
   * getCurrentUser, ...) already hit localStorage live, so they pick up the change on the next
   * change-detection tick, which the storage event triggers automatically.
   */
  private readonly onStorageChange = (event: StorageEvent): void => {
    if (event.key !== null && event.key !== TOKEN_STORAGE_KEY) {
      return;
    }
    this.scheduleExpiryCheck();
  };

  register(request: RegisterRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/register`, request)
      .pipe(tap(response => this.setTokenIfPresent(response.token)));
  }

  login(request: LoginRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/login`, request)
      .pipe(tap(response => this.setTokenIfPresent(response.token)));
  }

  verifyMfa(mfaToken: string, code: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/mfa/verify`, {mfaToken, code})
      .pipe(tap(response => this.setTokenIfPresent(response.token)));
  }

  /** `mfaToken` null = voluntary setup of a signed-in user. */
  startMfaSetup(mfaToken: string | null): Observable<MfaSetupData> {
    return this.http.post<MfaSetupData>(`${this.apiUrl}/mfa/setup`, {mfaToken});
  }

  confirmMfaSetup(mfaToken: string | null, code: string): Observable<MfaConfirmResponse> {
    return this.http.post<MfaConfirmResponse>(`${this.apiUrl}/mfa/setup/confirm`, {mfaToken, code})
      .pipe(tap(response => this.setTokenIfPresent(response.token)));
  }

  getMfaStatus(): Observable<MfaStatus> {
    return this.http.get<MfaStatus>(`${this.apiUrl}/mfa/status`);
  }

  disableMfa(password: string, code: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/mfa/disable`, {password, code});
  }

  regenerateRecoveryCodes(code: string): Observable<{ recoveryCodes: string[] }> {
    return this.http.post<{ recoveryCodes: string[] }>(`${this.apiUrl}/mfa/recovery-codes`, {code});
  }

  private setTokenIfPresent(token: string | null): void {
    if (token) {
      this.setToken(token);
    }
  }

  forgotPassword(email: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/forgot-password`, {email});
  }

  resetPassword(token: string, newPassword: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/reset-password`, {token, newPassword});
  }

  logout(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    this.clearExpiryTimer();
  }

  /** A token handed out by the server's X-New-Token header (silent renewal, or - since roles changed - a
   * reissue with the current roles). Returns true when the roles differ from the previous token's. */
  applyRenewedToken(token: string): boolean {
    const before = [...this.getRoles()].sort().join(',');
    this.setToken(token);
    return before !== [...this.getRoles()].sort().join(',');
  }

  setToken(token: string): void {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    this.scheduleExpiryCheck();
    this.loggedInSource.next();
  }

  /** Called by the auth interceptor on a 401, and by the expiry timer once the token's `exp` claim passes. */
  notifySessionExpired(): void {
    const hadToken = this.getToken() !== null;
    this.logout();
    if (hadToken) {
      this.sessionExpiredSource.next();
    }
  }

  private scheduleExpiryCheck(): void {
    this.clearExpiryTimer();
    const token = this.getToken();
    const exp = token ? this.decodePayload(token)?.exp : null;
    if (!exp) {
      return;
    }

    const msUntilExpiry = exp * 1000 - Date.now();
    if (msUntilExpiry <= 0) {
      this.notifySessionExpired();
      return;
    }
    this.expiryTimer = setTimeout(() => this.notifySessionExpired(), msUntilExpiry);
  }

  private clearExpiryTimer(): void {
    if (this.expiryTimer !== null) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = null;
    }
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  }

  isLoggedIn(): boolean {
    return this.getToken() !== null;
  }

  hasRole(role: string): boolean {
    return this.getRoles().includes(`ROLE_${role}`);
  }

  isAdmin(): boolean {
    return this.hasRole('ADMIN');
  }

  getCurrentUser(): CurrentUser | null {
    const token = this.getToken();
    if (!token) {
      return null;
    }
    const payload = this.decodePayload(token);
    if (!payload?.sub) {
      return null;
    }
    return {
      email: payload.sub,
      firstName: payload.firstName ?? null,
      lastName: payload.lastName ?? null,
      roles: (payload.roles ?? []).map(role => role.replace(/^ROLE_/, ''))
    };
  }

  private getRoles(): string[] {
    const token = this.getToken();
    if (!token) {
      return [];
    }
    return this.decodePayload(token)?.roles ?? [];
  }

  private decodePayload(token: string): JwtPayload | null {
    const payloadSegment = token.split('.')[1];
    if (!payloadSegment) {
      return null;
    }
    try {
      const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
      // atob() returns each decoded byte as one char code (Latin-1), which mangles multi-byte UTF-8
      // characters (e.g. "ü") unless re-decoded explicitly as UTF-8 via TextDecoder.
      const bytes = Uint8Array.from(atob(base64), char => char.charCodeAt(0));
      const json = new TextDecoder('utf-8').decode(bytes);
      return JSON.parse(json) as JwtPayload;
    } catch {
      return null;
    }
  }

}
